import { useState } from "react";

export type WandArgs = { label: string; window: string; method: string };

const WINDOWS: { value: string; label: string }[] = [
  { value: "PAST_12", label: "Past 12 months" },
  { value: "PAST_6", label: "Past 6 months" },
  { value: "FUTURE_6", label: "Future 6 months" },
  { value: "FUTURE_12", label: "Future 12 months" },
];
const METHODS: { value: string; label: string }[] = [
  { value: "EVEN", label: "Even (straight-line)" },
  { value: "PRORATED", label: "Prorated by days" },
];

const money = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

// U9 — the magic-wand: smooth one anchored payout over a window. Opened from a
// transaction row; recognizes the cost across the chosen months (a PAST window is
// backward accrual, FUTURE is forward deferral) while preserving the total. The
// before/after preview is the chain editor's terminal chart, which updates on save.
export default function TransformAnchorDialog({
  txn,
  onConfirm,
  onClose,
}: {
  txn: { md5: string; postingDate: string; description: string; amount: number };
  onConfirm: (args: WandArgs) => Promise<void>;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(txn.description.slice(0, 60));
  const [window, setWindow] = useState(txn.amount < 0 ? "PAST_12" : "FUTURE_12");
  const [method, setMethod] = useState("EVEN");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = () => {
    setSaving(true);
    setError(null);
    onConfirm({ label, window, method })
      .then(onClose)
      .catch((e: Error) => {
        setError(e.message);
        setSaving(false);
      });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-[560px] max-h-[85vh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}>
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Smooth this transaction</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            {txn.postingDate} · {money(txn.amount)} · <span className="text-gray-400">{txn.description}</span>
          </p>
        </div>

        <label className="block">
          <span className="text-xs text-gray-500">Label</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm" placeholder="e.g. Q4 bonus" />
        </label>

        <div>
          <span className="text-xs text-gray-500">Spread over</span>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {WINDOWS.map((w) => (
              <label key={w.value}
                className={`flex items-center gap-2 text-sm border rounded px-2 py-1.5 cursor-pointer ${
                  window === w.value ? "border-emerald-500 bg-emerald-50" : "border-gray-200"
                }`}>
                <input type="radio" name="window" checked={window === w.value}
                  onChange={() => setWindow(w.value)} />
                {w.label}
              </label>
            ))}
          </div>
        </div>

        <label className="block">
          <span className="text-xs text-gray-500">Method</span>
          <select value={method} onChange={(e) => setMethod(e.target.value)}
            className="mt-1 w-full border rounded px-2 py-1 text-sm">
            {METHODS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </label>

        <p className="text-xs text-gray-400">
          The total cost is preserved — it's recognized across the window instead of all
          at once. A past window won't recognize cost before this counterparty's first
          activity. The chain's terminal preview updates after you save.
        </p>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={onClose} disabled={saving}
            className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={confirm} disabled={saving}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
            {saving ? "Smoothing…" : "Smooth payout"}
          </button>
        </div>
      </div>
    </div>
  );
}
